import React, { useEffect, useState } from 'react';
import PopupContainer from '../components/PopupContainer';
import OptionButton from '../components/OptionButton';
import axios from 'axios';
import { tmAIAssistantDefaultValue } from '../constant';
import Loader from '../components/Loader';
import FloatingButton from '../components/FloatingButton';
import { useNavigate } from 'react-router-dom';
import { getSessionId, savePersona } from '../utils/sessionManager';
import ProgressSteps from '../components/progressStep';

export default function InteractionScreen({ language, sessionId, onComplete }) {
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const [currentStep, setCurrentStep] = useState(1);
  const [totalSteps, setTotalSteps] = useState(7); // default 7 steps

  // Load first question
  useEffect(() => {
    const id = getSessionId();
    axios
      .post(`${tmAIAssistantDefaultValue.tmAIChatbotApiUrl}chat`, {
        session_id: id?.sessionId,
        language,
        message: '',
      })
      .then((res) => {
        setData(res.data);

        if (res.data.current_question?.question_id) {
          const qid = res.data.current_question?.question_id;
          const stepNumber = qid ? parseInt(qid.replace(/\D/g, '')) : 1;
          setCurrentStep(stepNumber);
        }

        if (res.data.total_questions) {
          setTotalSteps(res.data.total_questions);
        }

        if (res.data.persona_status === 'completed') {
          savePersona(res.data);
          onComplete(res.data);
          navigate('/profile');
        }
      })
      .catch((err) => console.error('API Error:', err))
      .finally(() => setLoading(false));
  }, [language, sessionId, navigate]);

  // Handle option click
  const handleOptionClick = (answerKey) => {
    setLoading(true);
const id = getSessionId();
    axios
      .post(`${tmAIAssistantDefaultValue.tmAIChatbotApiUrl}chat`, {
        session_id: id?.sessionId,
        language,
        message: answerKey,
      })
      .then((res) => {
        if (res.data.persona_status === 'completed') {
          savePersona(res.data);
          onComplete(res.data);
          navigate('/profile');
        } else {
          setData(res.data);

          if (res.data.current_question?.question_id) {
            const qid = res.data.current_question?.question_id;
            const stepNumber = qid ? parseInt(qid.replace(/\D/g, '')) : 1;
            setCurrentStep(stepNumber);
          }
        }
      })
      .catch((err) => console.error('API Error:', err))
      .finally(() => setLoading(false));
  };

  // Loader UI
  if (loading) {
    return (
      <PopupContainer>
        <Loader />
      </PopupContainer>
    );
  }

  if (!data) return null;

  const speakPageContent = () => {
  const synth = window.speechSynthesis;
  if (!synth) return;

  // Toggle stop if already speaking
  if (isSpeaking) {
    synth.cancel();
    setIsSpeaking(false);
    return;
  }

  synth.cancel();

  const text = document.body.innerText;
  const utter = new SpeechSynthesisUtterance(text);

  // Force UK English
  utter.lang = 'en-GB';

  const setVoiceAndSpeak = () => {
    const voices = synth.getVoices();

    // Prefer high quality UK voices first
    const preferredUKVoice =
      voices.find(v => v.name.includes('Google UK English Female')) ||
      voices.find(v => v.name.includes('Google UK English Male')) ||
      voices.find(v => v.lang === 'en-GB');

    if (preferredUKVoice) {
      utter.voice = preferredUKVoice;
    }

    utter.onend = () => setIsSpeaking(false);

    synth.speak(utter);
    setIsSpeaking(true);
  };

  // Chrome loads voices asynchronously
  if (synth.getVoices().length === 0) {
    synth.onvoiceschanged = setVoiceAndSpeak;
  } else {
    setVoiceAndSpeak();
  }
};

const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.1, 2));
const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.1, 0.5));

const handleBackOption = () => {
  handleOptionClick('back');
};

  return (
    <>
    <PopupContainer handleBack={()=>handleBackOption()} screen='interaction'>
      {/* ---------------- PROGRESS BAR UI ---------------- */}
    <ProgressSteps currentStep={currentStep} />
      {/* -------------------------------------------------- */}

      <h1 className="title">{data.current_question?.heading}</h1>
      <p className="subtitle">{data.current_question?.subheading}</p>

      <h2 className="section-heading">{data.current_question?.text}</h2>

      <div className="options-row">
        {data.current_question?.options?.map((opt, index) => (
          <OptionButton
            index={index}
            key={opt.key}
            label={opt.label}
            color={opt.color}
            icon={opt.icon}
            onClick={() => handleOptionClick(opt.key)}
          />
        ))}
      </div>

      <FloatingButton
        screen=""
        isSpeaking={isSpeaking}
        onSpeak={speakPageContent}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
      />
    </PopupContainer>
      </>
  );
}
